import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { BookText, Loader2, Download } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function GrandLivre() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [compteDebut, setCompteDebut] = useState('');
  const [compteFin, setCompteFin] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateDebut) params.date_debut = dateDebut;
      if (dateFin) params.date_fin = dateFin;
      if (compteDebut) params.compte_debut = compteDebut;
      if (compteFin) params.compte_fin = compteFin;
      const res = await axios.get(`${API_URL}/comptabilite/grand-livre`, { params });
      setData(res.data);
    } catch (e) { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const totalDebit = data.reduce((s, c) => s + c.total_debit, 0);
  const totalCredit = data.reduce((s, c) => s + c.total_credit, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            <BookText className="w-6 h-6 inline mr-2" />Grand livre
          </h1>
          <p className="text-slate-500">{data.length} compte(s)</p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-5 gap-4">
            <div><Label>Date début</Label><Input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} /></div>
            <div><Label>Date fin</Label><Input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} /></div>
            <div><Label>Compte début</Label><Input placeholder="401000" value={compteDebut} onChange={e => setCompteDebut(e.target.value)} /></div>
            <div><Label>Compte fin</Label><Input placeholder="411999" value={compteFin} onChange={e => setCompteFin(e.target.value)} /></div>
            <div className="flex items-end"><Button onClick={fetchData} className="bg-[#0F5156] hover:bg-[#0d464a] w-full">Filtrer</Button></div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <>
          <Accordion type="multiple" className="space-y-2">
            {data.map(compte => (
              <AccordionItem key={compte.compte_numero} value={compte.compte_numero} className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center justify-between w-full pr-4">
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold text-[#0F5156]">{compte.compte_numero}</span>
                      <span className="font-medium">{compte.compte_libelle}</span>
                      <span className="text-sm text-slate-400">({compte.mouvements.length} mvt)</span>
                    </div>
                    <div className="flex gap-6 text-sm">
                      <span>D: <strong className="font-mono">{formatCurrency(compte.total_debit)}</strong></span>
                      <span>C: <strong className="font-mono">{formatCurrency(compte.total_credit)}</strong></span>
                      <span className={compte.solde >= 0 ? 'text-blue-600 font-semibold' : 'text-red-600 font-semibold'}>
                        Solde: {formatCurrency(Math.abs(compte.solde))} {compte.solde >= 0 ? 'D' : 'C'}
                      </span>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-24">Date</TableHead>
                        <TableHead className="w-16">Jnl</TableHead>
                        <TableHead className="w-16">N°</TableHead>
                        <TableHead>Libellé</TableHead>
                        <TableHead className="w-24">Pièce</TableHead>
                        <TableHead className="w-28 text-right">Débit</TableHead>
                        <TableHead className="w-28 text-right">Crédit</TableHead>
                        <TableHead className="w-20">Lettrage</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {compte.mouvements.map((m, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-sm">{m.date}</TableCell>
                          <TableCell className="text-sm">{m.journal}</TableCell>
                          <TableCell className="font-mono text-sm">{m.numero}</TableCell>
                          <TableCell>{m.libelle}</TableCell>
                          <TableCell className="text-sm text-slate-500">{m.piece_ref || ''}</TableCell>
                          <TableCell className="text-right font-mono">{m.debit > 0 ? formatCurrency(m.debit) : ''}</TableCell>
                          <TableCell className="text-right font-mono">{m.credit > 0 ? formatCurrency(m.credit) : ''}</TableCell>
                          <TableCell className="text-sm">{m.lettrage || ''}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {data.length > 0 && (
            <Card>
              <CardContent className="py-4">
                <div className="flex justify-end gap-8 text-sm font-semibold">
                  <span>Total Débit : <span className="font-mono text-lg">{formatCurrency(totalDebit)}</span></span>
                  <span>Total Crédit : <span className="font-mono text-lg">{formatCurrency(totalCredit)}</span></span>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
